import { Button } from '@/components/catalyst-ui/button'
import { EyeIcon16, CrossIcon16, ShieldCheck16, EditIcon16, GreenTick20 as GreenTickIcon20 } from '@/assets/icons'
import { useWhoisPrivacyState } from '../hooks/useMyDomains'
export default function DomainInfoCard({ domain = {}, domainName: propDomainName, onEditAddress, onEditNameservers, onEditAutoRenewal, onEditPrivacy, isViewer = false }) {


  // Early return if domain is null or undefined
  if (!domain) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:w-[1096px]">
        <div className="p-4 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <div>Loading domain information...</div>
          </div>
        </div>
      </div>
    )
  }

  // Provide safe defaults for potentially undefined nested properties
  // Handle both old structure and new detailed API structure
  const renewal = domain?.renewal || {}
  const nameservers = domain?.nameservers || domain?.data?.nameservers || []
  
  // Handle contacts - they might be in different structures
  const contacts = domain?.data?.contacts || {}
  const registrant = domain?.registrant || contacts?.owner || contacts?.registrant || {}
  
  // Handle auto renewal from different possible locations
  // Check multiple sources: domain.autoRenew (API), domain.auto_renew (DB), domain.data.autoRenew
  const autoRenewal = 
    domain?.autoRenew || 
    domain?.auto_renew || 
    domain?.data?.autoRenew || 
    domain?.data?.auto_renew ||
    domain?.dbAutoRenew ||
    domain?.data?.dbAutoRenew ||
    false

  // Determine the canonical domain name to query (prefer the explicit prop if provided)
  const domainName = propDomainName || domain?.name || domain?.domain || domain?.data?.domain || domain?.data?.domainName || domain?.domainName;

  // Fetch WHOIS privacy state
  const { data: whoisQueryResp } = useWhoisPrivacyState(domainName, { enabled: !!domainName });
  
  // Simple check: use API response or domain data
  const whoisPrivacyState = whoisQueryResp?.data?.whoisPrivacyState || domain?.data?.whoisPrivacyState || domain?.data?.whois_privacy;
  const isPrivate = whoisPrivacyState === 'enable' || whoisPrivacyState === true || String(whoisPrivacyState).toLowerCase() === 'true';
  // console.log('🔍 DomainInfoCard - whoisPrivacyRaw:', whoisPrivacyRaw, '-> whoisPrivacyState:', whoisPrivacyState);
  
  // Handle registry dates from the new API structure
  const createDate = renewal.createDate || domain?.data?.registryCreateDate || 'Not available'
  const expiryDate = renewal.expiryDate || domain?.data?.registryExpirationDate || 'Not available'

  return (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 lg:gap-12">
  {/* Auto Renewal Card */}
  <div className="px-4 ">
    <div className="flex flex-col justify-between mb-2">
      <span className="text-sm text-[#71717B] font-normal">Renewal</span>
      {isViewer ? (
        <div className='flex items-center justify-start w-fit -ml-3'>
          {autoRenewal ? (
            <>
              <GreenTickIcon20 className='w-[18px] h-[18px]' />
              <u><span className="text-sm font-medium text-gray-900">Auto Renewal</span></u>
            </>
          ) : (
            <>
              <CrossIcon16 className='w-[18px] h-[18px]' alt="disabled" />
              <u><span className="text-sm font-medium text-gray-900 "> Disabled</span></u>
            </>
          )}
        </div>
      ) : (
        <Button className='flex items-center justify-start cursor-pointer w-fit -ml-3' 
          plain
          onClick={onEditAutoRenewal}
        >
           {autoRenewal ? (
             <>
               <GreenTickIcon20 className='w-[18px] h-[18px]' />
               <u><span className="text-sm font-medium text-gray-900">Auto Renewal</span></u>
             </>
           ) : (
             <>
               <CrossIcon16 className='w-[18px] h-[18px]' alt="disabled" />
               <u><span className="text-sm font-medium text-gray-900 "> Disabled</span></u>
             </>
           )}
           <EditIcon16 className='w-[16px] h-[16px] cursor-pointer' />
        </Button>
      )}
    </div>
  </div>

  {/* WHOIS Privacy Service Card */}
  <div className="px-4 ">
    <div className="flex flex-col items-start justify-between mb-2">
      <span className="text-sm text-[#71717B]">WHOIS Privacy Service</span>
      {isViewer ? (
        <div className="flex items-center justify-start w-fit -ml-3">
          {isPrivate ? (
            <>
              <ShieldCheck16 className='w-[16px] h-[16px]' alt="privacy enabled" />
              <u><span className="text-sm text-[#0F172A] font-medium">Private</span></u>
            </>
          ) : (
            <>
              <EyeIcon16 className='w-[16px] h-[16px]' alt="public" />
              <u><span className="text-sm text-[#0F172A] font-medium">Public</span></u>
            </>
          )}
        </div>
      ) : (
        <Button className="flex items-center justify-start cursor-pointer w-fit -ml-3" 
          plain
          onClick={onEditPrivacy}
        >
          {isPrivate ? (
            <>
              <ShieldCheck16 className='w-[16px] h-[16px]' alt="privacy enabled" />
              <u><span className="text-sm text-[#0F172A] font-medium">Private</span></u>
            </>
          ) : (
            <>
              <EyeIcon16 className='w-[16px] h-[16px]' alt="public" />
              <u><span className="text-sm text-[#0F172A] font-medium">Public</span></u>
            </>
          )}
          <EditIcon16 
            className='w-[16px] h-[16px] cursor-pointer' 
            alt="edit privacy"
          />
        </Button>
      )}
    </div>
  </div>

  {/* Renewal Price Estimate */}
  <div className="px-4 ">
    <h3 className="text-sm text-[#71717B] mb-2">Renewal Price Estimate</h3>
    {isPrivate && renewal.price && renewal.price !== 'Not available' ? (
      <div className="space-y-1">
        <div className="text-sm font-medium">
          {(() => {
            // Try to extract numeric value from string like "$12.00" or "12.00"
            let basePrice = 0;
            if (typeof renewal.price === 'string') {
              const match = renewal.price.match(/[\d.]+/);
              if (match) {
                basePrice = parseFloat(match[0]);
              }
            } else if (typeof renewal.price === 'number') {
              basePrice = renewal.price;
            }
            
            if (basePrice > 0) {
              return `$${(basePrice + 3.00).toFixed(2)}`;
            }
            return renewal.price;
          })()}
        </div>
        <div className="text-xs text-[#71717B] space-y-1 max-w-40 mt-1">
          <div className="flex justify-between">
            <span>Domain:</span>
            <span>{renewal.price}</span>
          </div>
          <div className="flex justify-between">
            <span>WHOIS Privacy:</span>
            <span>$3.00</span>
          </div>
        </div>
      </div>
    ) : (
      <div className="text-sm font-medium">{renewal.price || 'Not available'}</div>
    )}
  </div>

  {/* Registrant Contact */}
  <div className="p-4 border-t border-[#09090B1A]">
    <div className="flex items-start justify-between mb-4">
      <h3 className="text-base font-medium text-gray-900">Registrant Contact</h3>
    </div>
    <div className="space-y-1 mb-2 text-sm text-[#71717B]">
      <div>
        <div>
          {registrant.name || 
           registrant.full_name ||
           (registrant.first_name && registrant.last_name ? `${registrant.first_name} ${registrant.last_name}` : 
           (registrant.firstName && registrant.lastName ? `${registrant.firstName} ${registrant.lastName}` : 'Not available'))}
        </div>
      </div>
      <div>
        <div>{registrant.addressLine1 || registrant.address1 || 'Not available'}</div>
      </div>
      {(registrant.addressLine2 || registrant.address2) && (
        <div>
          <div>{registrant.addressLine2 || registrant.address2}</div>
        </div>
      )}
      <div>
        <div>{registrant.country || (registrant.city && registrant.country ? `${registrant.city}, ${registrant.country}` : 'Not available')}</div>
      </div>
      <div>
        <div>{registrant.phone || 'Not available'}</div>
      </div>
      <div>
        <div>{registrant.email || 'Not available'}</div>
      </div>
    </div>
    {!isViewer && onEditAddress && (
      <Button outline onClick={onEditAddress} className="data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2">Edit</Button>
    )}
     
  </div>

  {/* Nameservers */}
  <div className="p-4 border-t border-[#09090B1A]">
    <div className="flex items-start justify-between mb-4 ">
      <h3 className="text-base font-medium text-gray-900">Nameservers</h3>
    </div>
    <div className="space-y-1 mb-2 text-sm text-[#71717B]">
      {nameservers.length > 0 ? nameservers.map((ns, index) => (
        <div
          key={index}
          className="text-sm text-[#71717B] font-normal "
        >
          {typeof ns === 'string' ? ns : ns?.name || ns}
        </div>
      )) : (
        <div className="text-sm text-[#71717B] font-normal">No nameservers configured</div>
      )}
    </div>
    {!isViewer && onEditNameservers && (
      <Button className="my-2 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2" outline onClick={onEditNameservers}>Edit</Button>
    )}

  </div>

  {/* Registry Dates */}
  <div className="p-4 border-t flex flex-col gap-1 border-[#09090B1A]">
    <h3 className="text-sm font-normal text-[#71717B]">Registry Create Date</h3>
    <div className="text-sm font-medium text-black mb-5">{createDate}</div>
    <h3 className="text-sm font-normal text-[#71717B]">Registry Expire Date</h3>
    <div className="text-sm font-medium text-black">{expiryDate}</div>
  </div>
</div>

  )
}
